import { PURGE } from 'redux-persist';

const initialState = {
  allCampaigns: [],
  campaignsLoading: false,
  campaignsError: null,
  campaignsTotal: 0,
  campaignsPage: 1,
  campaignsLimit: 20,

  singleCampaign: {},
  singleCampaignLoading: false,

  templates: [],
  templatesLoading: false,
  singleTemplate: null,

  dashboardStats: null,
  dashboardStatsLoading: false,

  analytics: null,
  analyticsLoading: false,
  activityFeed: [],
  activityFeedLoading: false,
  auditLog: [],
  auditLogLoading: false,
  auditLogTotal: 0,

  campaignContacts: [],
  campaignContactsLoading: false,
  campaignContactsTotal: 0,
  campaignContactStats: null,
  executionProgress: null,

  filters: { status: '', type: '', search: '', sortBy: 'updatedAt', sortOrder: 'desc' },

  sequenceNodes: [],
  sequenceEdges: [],
  selectedNode: null,
  selectedStep: 0,

  campaignBuilderModal: false,

  // Milo AI Chat
  miloSessionId: null,
  miloCampaignId: null,
  miloMessages: [],
  miloSuggestions: [],
  miloLoading: false,
  miloAgentActions: [],

  // AI Personalization
  personalizationLoading: false,
  personalizedMessageResult: null,
  selectedTestContact: null,
  streamingText: '', // For streaming AI response

  // Test campaign
  testCampaign: { loading: false, success: false, error: null, totalSteps: 0, currentStep: 0, status: null, message: '', stepProgress: [], fromEmail: null, contactId: null, contactName: null, emailPreview: null },

  // Content Analysis & Improvement
  analyzing: false,
  contentAnalysis: null,
  autoImproving: false,
  autoImproveResult: null,

  // AI Variables
  aiVariablesLoading: false,
  aiVariablesColumns: {},          // Session overlay: { columnName: { contactId: value } } populated by run callbacks.
  audienceColumnVisibility: {},    // Persisted on Campaign: { propertyName: boolean }. Presence of key = attached; value = visible.
  aiVariableKeyStatus: { openai: false, anthropic: false, xai: false, deepseek: false },

  isLoading: false,
  error: null
};

const Campaign = (state = initialState, { type, payload }) => {
  switch (type) {
    case PURGE:
      return initialState;

    case 'GET_ALL_CAMPAIGNS':
      return { ...state, campaignsLoading: true, campaignsError: null };

    case 'SET_ALL_CAMPAIGNS':
      return { ...state, campaignsLoading: false, allCampaigns: payload['campaigns'] || payload, campaignsTotal: payload['pagination']?.['total'] || 0, campaignsPage: payload['pagination']?.['page'] || 1 };

    case 'GET_SINGLE_CAMPAIGN':
      return { ...state, singleCampaignLoading: true };

    case 'SET_SINGLE_CAMPAIGN':
      return { ...state, singleCampaignLoading: false, singleCampaign: payload || {} };

    case 'CREATE_CAMPAIGN':
    case 'UPDATE_CAMPAIGN':
    case 'DELETE_CAMPAIGN':
    case 'DUPLICATE_CAMPAIGN':
    case 'LAUNCH_CAMPAIGN':
    case 'PAUSE_CAMPAIGN':
    case 'RESUME_CAMPAIGN':
    case 'COMPLETE_CAMPAIGN':
      return { ...state, isLoading: true };

    case 'CREATE_CAMPAIGN_SUCCESS':
      return { ...state, isLoading: false, allCampaigns: [payload, ...state['allCampaigns']] };

    case 'UPDATE_CAMPAIGN_SUCCESS':
      return { ...state, isLoading: false, singleCampaign: payload, allCampaigns: state['allCampaigns'].map(c => c['_id'] === payload['_id'] ? payload : c) };

    case 'DELETE_CAMPAIGN_SUCCESS':
      return { ...state, isLoading: false, allCampaigns: state['allCampaigns'].filter(c => c['_id'] !== payload) };

    case 'GENERATE_CAMPAIGN':
      return { ...state, isLoading: true };

    case 'GET_DASHBOARD_STATS':
      return { ...state, dashboardStatsLoading: true };

    case 'SET_DASHBOARD_STATS':
      return { ...state, dashboardStatsLoading: false, dashboardStats: payload };

    case 'GET_CAMPAIGN_ANALYTICS':
      return { ...state, analyticsLoading: true };

    case 'SET_CAMPAIGN_ANALYTICS':
      return { ...state, analyticsLoading: false, analytics: payload };

    case 'GET_ACTIVITY_FEED':
      return { ...state, activityFeedLoading: true };

    case 'SET_ACTIVITY_FEED':
      return { ...state, activityFeedLoading: false, activityFeed: payload };

    case 'GET_CAMPAIGN_AUDIT_LOG':
      return { ...state, auditLogLoading: true };

    case 'SET_CAMPAIGN_AUDIT_LOG':
      return { ...state, auditLogLoading: false, auditLog: payload?.['entries'] || [], auditLogTotal: payload?.['total'] || 0 };

    case 'GET_CAMPAIGN_TEMPLATES':
      return { ...state, templatesLoading: true };

    case 'SET_CAMPAIGN_TEMPLATES':
      return { ...state, templatesLoading: false, templates: payload };

    case 'GET_CAMPAIGN_TEMPLATE':
      return { ...state, templatesLoading: true };

    case 'SET_CAMPAIGN_TEMPLATE':
      return { ...state, templatesLoading: false, singleTemplate: payload };

    case 'SET_CAMPAIGN_FILTERS':
      return { ...state, filters: { ...state['filters'], ...payload } };

    case 'SET_CAMPAIGN_BUILDER_MODAL':
      return { ...state, campaignBuilderModal: payload };

    case 'SET_SELECTED_STEP':
      return { ...state, selectedStep: payload };

    case 'SET_SEQUENCE_NODES':
      return { ...state, sequenceNodes: payload };

    case 'SET_SEQUENCE_EDGES':
      return { ...state, sequenceEdges: payload };

    case 'SET_CAMPAIGN_SELECTED_NODE':
      return { ...state, selectedNode: payload };

    case 'SET_CAMPAIGN_LOADING':
      return { ...state, isLoading: payload };

    case 'SET_CAMPAIGN_ERROR':
      return { ...state, isLoading: false, analyticsLoading: false, error: payload };

    // Milo AI Chat
    case 'START_MILO_SESSION':
      return { ...state, miloLoading: true };

    case 'SET_MILO_SESSION': {
      // If we have existing messages from history, use them; otherwise create greeting message
      let messages = [];
      if (payload['messages'] && payload['messages'].length > 0) {
        messages = payload['messages'].map((m, idx) => ({ id: m['_id'] || `history_${idx}`, role: m['role'], content: m['content'], toolsUsed: m['toolsUsed'] || [], timestamp: m['timestamp'] }));
      } else if (payload['greeting']) {
        messages = [{ id: 'greeting', role: 'assistant', content: payload['greeting'] }];
      }
      return { ...state, miloSessionId: payload['sessionId'], miloCampaignId: payload['campaignId'], miloMessages: messages, miloSuggestions: payload['suggestions'] || [], miloLoading: false };
    }

    case 'SEND_MILO_MESSAGE':
      return { ...state, miloLoading: true, miloAgentActions: [] };

    case 'SET_MILO_MESSAGES':
      return { ...state, miloMessages: payload };

    case 'ADD_MILO_MESSAGE':
      return { ...state, miloMessages: [...state['miloMessages'], payload], miloLoading: false };

    case 'SET_MILO_SUGGESTIONS':
      return { ...state, miloSuggestions: payload };

    case 'SET_MILO_LOADING':
      return { ...state, miloLoading: payload };

    case 'SET_MILO_AGENT_ACTIONS':
      return { ...state, miloAgentActions: payload };

    case 'ADD_MILO_AGENT_ACTION':
      const matchKey = payload['toolName'] ? 'toolName' : 'actionType';
      const existingIdx = state['miloAgentActions'].findIndex(a => a[matchKey] === payload[matchKey] && a['runId'] === payload['runId']);
      if (existingIdx >= 0) {
        const updated = [...state['miloAgentActions']];
        updated[existingIdx] = payload;
        return { ...state, miloAgentActions: updated };
      }
      return { ...state, miloAgentActions: [...state['miloAgentActions'], payload] };

    case 'CLEAR_MILO_AGENT_ACTIONS':
      return { ...state, miloAgentActions: [] };

    case 'RESET_MILO_SESSION':
      return { ...state, miloSessionId: null, miloCampaignId: null, miloMessages: [], miloSuggestions: [], miloLoading: false, miloAgentActions: [] };

    // AI Personalization
    case 'GENERATE_PERSONALIZED_MESSAGE':
      return { ...state, personalizationLoading: true, streamingText: '' };

    case 'GENERATE_PERSONALIZED_MESSAGE_STREAM':
      return { ...state, personalizationLoading: true, streamingText: '' };

    case 'SET_STREAMING_TEXT':
      return { ...state, streamingText: payload };

    case 'SET_PERSONALIZED_MESSAGE_RESULT':
      return { ...state, personalizationLoading: false, personalizedMessageResult: payload, streamingText: '' };

    case 'SET_SELECTED_TEST_CONTACT':
      return { ...state, selectedTestContact: payload };

    case 'SET_PERSONALIZATION_LOADING':
      return { ...state, personalizationLoading: payload };

    // Test campaign
    case 'SEND_TEST_CAMPAIGN':
      return { ...state, testCampaign: { loading: true, success: false, error: null, totalSteps: 0, currentStep: 0, status: 'initializing', message: 'Initializing test...', stepProgress: [], fromEmail: null, contactId: null, contactName: null, emailPreview: null } };

    case 'SET_TEST_CAMPAIGN_RESPONSE':
      return { ...state, testCampaign: { ...state['testCampaign'], loading: false, success: payload['success'] || false, error: payload['error'] || null } };

    case 'SET_TEST_CAMPAIGN_PROGRESS': {
      const isComplete = ['completed', 'failed'].includes(payload['status']);
      return { ...state, testCampaign: { ...state['testCampaign'], totalSteps: payload['totalSteps'] || state['testCampaign']['totalSteps'], currentStep: payload['currentStep'] || state['testCampaign']['currentStep'], status: payload['status'], message: payload['message'] || '', stepProgress: payload['results'] || state['testCampaign']['stepProgress'], fromEmail: payload['fromEmail'] || state['testCampaign']['fromEmail'], contactId: payload['contactId'] || state['testCampaign']['contactId'], contactName: payload['contactName'] || state['testCampaign']['contactName'], emailPreview: payload['emailPreview'] || state['testCampaign']['emailPreview'], loading: !isComplete, success: payload['status'] === 'completed', error: payload['status'] === 'failed' ? payload['message'] : null } };
    }

    case 'RESET_TEST_CAMPAIGN':
      return { ...state, testCampaign: { loading: false, success: false, error: null, totalSteps: 0, currentStep: 0, status: null, message: '', stepProgress: [], fromEmail: null, contactId: null, contactName: null, emailPreview: null } };

    // Content Analysis & Improvement
    case 'SET_ANALYZING':
      return { ...state, analyzing: payload };

    case 'SET_CONTENT_ANALYSIS':
      return { ...state, analyzing: false, contentAnalysis: payload };

    case 'SET_AUTO_IMPROVING':
      return { ...state, autoImproving: payload };

    case 'SET_AUTO_IMPROVE_RESULT':
      return { ...state, autoImproving: false, autoImproveResult: payload };

    // Campaign Contacts & Execution
    case 'GET_CAMPAIGN_CONTACTS':
      return { ...state, campaignContactsLoading: true };

    case 'SET_CAMPAIGN_CONTACTS':
      // contactStats is set only on filtered fetches — null means "use the campaign-wide analytics stats"
      return { ...state, campaignContactsLoading: false, campaignContacts: payload['contacts'] || [], campaignContactsTotal: payload['totalCount'] || 0, campaignContactsStats: payload['contactStats'] || null };

    case 'SET_CAMPAIGN_CONTACT_STATS':
      return { ...state, campaignContactStats: payload };

    case 'SET_CAMPAIGN_EXECUTION_PROGRESS':
      return { ...state, executionProgress: payload };

    // AI Variables
    case 'SET_AI_VARIABLES_LOADING':
      return { ...state, aiVariablesLoading: payload };

    case 'SET_AI_VARIABLES_RESULT': {
      // Convert array [{ contactId, value }] to map { contactId: value }
      const map = {};
      (payload.results || []).forEach(r => { map[r.contactId] = r.value; });
      // Don't store __preview__ sentinel in columns (used by Test Preview only)
      if (payload.columnName === '__preview__') {
        return { ...state, aiVariablesLoading: false };
      }
      return { ...state, aiVariablesLoading: false, aiVariablesColumns: { ...state.aiVariablesColumns, [payload.columnName]: map } };
    }

    case 'SET_AI_VARIABLE_KEY_STATUS':
      return { ...state, aiVariableKeyStatus: payload };

    case 'SET_AUDIENCE_COLUMN_VISIBILITY':
      return { ...state, audienceColumnVisibility: payload || {} };

    default:
      return state;
  }
};

export default Campaign;
